import React, { useEffect, useState } from 'react';
import { Plus, X, ExternalLink } from 'lucide-react';
import { RegistryItem, RegistryStatus } from '../../lib/types';
import { apiListRegistry, apiCreateRegistryItem, apiUpdateRegistryItem, apiDeleteRegistryItem } from '../../lib/api';

const STATUS_COLORS: Record<RegistryStatus, { bg: string; text: string }> = {
  INBOX: { bg: '#EDE9FE', text: '#7C3AED' },
  QUEUED: { bg: '#DBEAFE', text: '#2563EB' },
  DONE: { bg: '#D1FAE5', text: '#059669' },
  REJECTED: { bg: '#FEE2E2', text: '#DC2626' },
};

const EFFORT_LABELS = { low: 'Low', medium: 'Med', high: 'High' };
const EFFORT_COLORS = { low: 'text-green-600', medium: 'text-amber-600', high: 'text-red-600' };

/**
 * RegistryPanel — implementation registry with editable status.
 * Columns: Name, Category, Status (editable dropdown), Effort.
 * Click row to expand detail. Add new items with the + button.
 */
export default function RegistryPanel() {
  const [items, setItems] = useState<RegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RegistryItem | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [filterStatus, setFilterStatus] = useState<RegistryStatus | 'ALL'>('ALL');

  useEffect(() => {
    apiListRegistry()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id: string, status: RegistryStatus) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    await apiUpdateRegistryItem(id, { status }).catch(() => {});
  };

  const addItem = async (item: Partial<RegistryItem>) => {
    const created = await apiCreateRegistryItem(item);
    setItems((prev) => [created, ...prev]);
    setShowAdd(false);
  };

  const deleteItem = async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    if (selected?.id === id) setSelected(null);
    await apiDeleteRegistryItem(id).catch(() => {});
  };

  const filtered = filterStatus === 'ALL' ? items : items.filter((i) => i.status === filterStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full" data-testid="registry-panel">
      {/* List */}
      <div className={`flex flex-col ${selected ? 'flex-1' : 'w-full'} border-r border-gray-100`}>
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            {(['ALL', 'INBOX', 'QUEUED', 'DONE', 'REJECTED'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${
                  filterStatus === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`registry-filter-${s}`}
              >
                {s === 'ALL' ? `All (${items.length})` : `${s} (${items.filter((i) => i.status === s).length})`}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 transition-colors"
            data-testid="registry-add-button"
          >
            <Plus size={13} />
            Add Item
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Effort</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const sc = STATUS_COLORS[item.status];
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${selected?.id === item.id ? 'bg-gray-50' : ''}`}
                    onClick={() => setSelected(selected?.id === item.id ? null : item)}
                    data-testid={`registry-item-${item.id}`}
                  >
                    <td className="py-3 px-6 font-medium text-gray-900">{item.name}</td>
                    <td className="py-3 px-4 text-gray-500 text-xs">{item.category}</td>
                    <td className="py-3 px-4">
                      <select
                        value={item.status}
                        onChange={(e) => { e.stopPropagation(); updateStatus(item.id, e.target.value as RegistryStatus); }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-gray-300"
                        style={{ backgroundColor: sc.bg, color: sc.text }}
                        data-testid={`registry-status-${item.id}`}
                      >
                        {(['INBOX', 'QUEUED', 'DONE', 'REJECTED'] as RegistryStatus[]).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`py-3 px-4 text-xs font-semibold ${EFFORT_COLORS[item.effort] || 'text-gray-600'}`}>
                      {EFFORT_LABELS[item.effort] || item.effort}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-sm text-gray-400">No items</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="w-72 shrink-0 p-5" data-testid="registry-detail">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 leading-snug pr-4">{selected.name}</h3>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600 shrink-0">
              <X size={15} />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Category</p>
              <p className="text-sm text-gray-700">{selected.category}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Effort</p>
              <p className={`text-sm font-semibold ${EFFORT_COLORS[selected.effort]}`}>
                {EFFORT_LABELS[selected.effort]}
              </p>
            </div>
            {selected.description && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Description</p>
                <p className="text-xs text-gray-600 leading-relaxed">{selected.description}</p>
              </div>
            )}
            <div className="pt-2 border-t border-gray-100">
              <button
                onClick={() => deleteItem(selected.id)}
                className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
                data-testid={`registry-delete-${selected.id}`}
              >
                Delete item
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && <AddItemModal onAdd={addItem} onCancel={() => setShowAdd(false)} />}
    </div>
  );
}

// ── Add Item Modal ─────────────────────────────────────────────────────────────

function AddItemModal({ onAdd, onCancel }: { onAdd: (item: Partial<RegistryItem>) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [effort, setEffort] = useState<'low' | 'medium' | 'high'>('medium');
  const [desc, setDesc] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" data-testid="add-registry-modal">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">Add Registry Item</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" autoFocus
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="registry-item-name-input" />
          <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="registry-item-category-input" />
          <select value={effort} onChange={(e) => setEffort(e.target.value as any)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="registry-item-effort-select">
            <option value="low">Low effort</option>
            <option value="medium">Medium effort</option>
            <option value="high">High effort</option>
          </select>
          <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" rows={2}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300 resize-none"
            data-testid="registry-item-desc-input" />
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={() => { if (!name.trim()) return; onAdd({ name: name.trim(), category: category.trim() || 'General', effort, description: desc.trim() || undefined, status: 'INBOX' }); }}
            disabled={!name.trim()} className="flex-1 rounded-lg bg-gray-900 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-40"
            data-testid="confirm-add-registry-item">
            Add Item
          </button>
        </div>
      </div>
    </div>
  );
}
